import React, { useState, useEffect, useId } from 'react';
import './ThemeToggle.css';

const MoonIcon = ({ size = 16, maskId }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <mask id={maskId}>
        <rect width="24" height="24" fill="white" />
        <circle cx="15.5" cy="8.5" r="7" fill="black" />
      </mask>
    </defs>
    <circle cx="11" cy="12" r="8" fill="currentColor" mask={`url(#${maskId})`} />
  </svg>
);

const SunIcon = ({ size = 16, strokeWidth = 1.8 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="4" fill="currentColor" fillOpacity="0.3" />
    <line x1="12" y1="2" x2="12" y2="4" />
    <line x1="12" y1="20" x2="12" y2="22" />
    <line x1="4.93" y1="4.93" x2="6.34" y2="6.34" />
    <line x1="17.66" y1="17.66" x2="19.07" y2="19.07" />
    <line x1="2" y1="12" x2="4" y2="12" />
    <line x1="20" y1="12" x2="22" y2="12" />
    <line x1="6.34" y1="17.66" x2="4.93" y2="19.07" />
    <line x1="19.07" y1="4.93" x2="17.66" y2="6.34" />
  </svg>
);

export default function ThemeToggle({ className = '', variant = 'default' }) {
  const [isDark, setIsDark] = useState(false);
  const [mounted, setMounted] = useState(false);
  const uid = useId();
  const maskId = `crescent-${uid.replace(/:/g, '')}`;

  useEffect(() => {
    setMounted(true);
    const savedTheme = localStorage.getItem('mg-theme');
    const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const shouldDark = savedTheme === 'dark' || (!savedTheme && systemDark);
    setIsDark(shouldDark);
    if (shouldDark) {
      document.documentElement.setAttribute('data-theme', 'dark');
    }
  }, []);

  const toggle = () => {
    const newDark = !isDark;
    setIsDark(newDark);
    if (newDark) {
      document.documentElement.setAttribute('data-theme', 'dark');
      localStorage.setItem('mg-theme', 'dark');
    } else {
      document.documentElement.removeAttribute('data-theme');
      localStorage.setItem('mg-theme', 'light');
    }
  };

  if (!mounted) return null;

  if (variant === 'circular') {
    return (
      <button
        type="button"
        className={`theme-toggle theme-toggle--circular ${isDark ? 'theme-toggle--dark' : 'theme-toggle--light'} ${className}`}
        onClick={toggle}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        <span className="theme-toggle__thumb">
          {isDark ? <MoonIcon size={16} maskId={maskId} /> : <SunIcon size={16} strokeWidth={2} />}
        </span>
      </button>
    );
  }

  if (variant === 'compact') {
    return (
      <button
        type="button"
        className={`theme-toggle theme-toggle--compact ${isDark ? 'theme-toggle--dark' : 'theme-toggle--light'} ${className}`}
        onClick={toggle}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        <span className="theme-toggle__slider">
          <span className="theme-toggle__thumb">
            {isDark ? <MoonIcon size={12} maskId={maskId} /> : <SunIcon size={12} strokeWidth={2} />}
          </span>
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      className={`theme-toggle ${isDark ? 'theme-toggle--dark' : 'theme-toggle--light'} ${className}`}
      onClick={toggle}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      <span className="theme-toggle__slider">
        <span className="theme-toggle__thumb">
          {isDark ? <MoonIcon size={14} maskId={maskId} /> : <SunIcon size={14} strokeWidth={2} />}
        </span>
      </span>
    </button>
  );
}